// Settings → Billing — the dedicated billing page (distinct from Packages, which
// is plan selection). Shows the current plan/status and routes the transactional
// bits (payment method, invoices, cancellation) to the Stripe billing portal.
// Plan CHANGES live on the Packages page; this page is "manage what I already have".
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Button } from "../ui/button";
import { openBillingPortal } from "../../morrowBilling";
import { useEntitlements } from "../../morrowEntitlements";
import { SettingsPageContainer, SettingsSection, SettingsRow } from "./settingsLayout";

export function BillingSettings() {
  const entitlements = useEntitlements();
  const navigate = useNavigate();
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalNote, setPortalNote] = useState<string | null>(null);

  // A Stripe billing portal only exists once there's a paying customer. Free /
  // never-subscribed subjects have no customer, so the portal 404s — surface that
  // as a helpful message + upgrade path instead of a dead button.
  const hasPaidPlan = entitlements.isLoaded && entitlements.tier !== "free";

  const openPortal = useCallback(async () => {
    setPortalBusy(true);
    setPortalNote(null);
    const url = await openBillingPortal();
    if (url) {
      window.location.href = url;
      return;
    }
    setPortalBusy(false);
    setPortalNote(
      "No billing is set up yet — this opens once you're on a paid plan. Upgrade to manage payment & invoices.",
    );
  }, []);

  const goToPackages = useCallback(() => void navigate({ to: "/settings/packages" }), [navigate]);

  const planLabel = entitlements.isLoaded
    ? entitlements.viewer.isOrg
      ? `${entitlements.planName} · your organization`
      : `${entitlements.planName} · ${entitlements.tier} tier`
    : "Loading…";

  const changePlan = (
    <Button variant="ghost-muted" size="sm" onClick={goToPackages}>
      Change plan
    </Button>
  );

  return (
    <SettingsPageContainer>
      <SettingsSection title="Billing" headerAction={changePlan}>
        <div className="px-3 sm:px-4">
          <p className="text-[13px] leading-[1.45] text-muted-foreground/80">
            Manage your subscription, payment method, and invoices.
          </p>
        </div>
        <SettingsRow
          title="Current plan"
          description={planLabel}
          control={
            <Button variant="ghost-muted" size="sm" onClick={goToPackages}>
              Compare plans
            </Button>
          }
        />
        {hasPaidPlan ? (
          <>
            <SettingsRow
              title="Payment method"
              description="Update the card on file, securely via Stripe."
              control={
                <Button size="sm" disabled={portalBusy} onClick={() => void openPortal()}>
                  {portalBusy ? "Opening…" : "Open billing portal"}
                </Button>
              }
            />
            <SettingsRow
              title="Invoices & receipts"
              description="Download past invoices from the billing portal."
              control={
                <Button
                  variant="ghost-muted"
                  size="sm"
                  disabled={portalBusy}
                  onClick={() => void openPortal()}
                >
                  View invoices
                </Button>
              }
            />
            <SettingsRow
              title="Cancel subscription"
              description="Cancel or pause your plan. You keep access until the period ends."
              control={
                <Button
                  variant="ghost-muted"
                  size="sm"
                  disabled={portalBusy}
                  onClick={() => void openPortal()}
                >
                  Manage in portal
                </Button>
              }
            />
          </>
        ) : (
          <SettingsRow
            title="Payment & invoices"
            description="You're on the Free plan — there's no billing to manage yet. Upgrade to add a payment method and get invoices."
            control={
              <Button size="sm" onClick={goToPackages}>
                Upgrade
              </Button>
            }
          />
        )}
        {portalNote ? (
          <div className="px-3 sm:px-4">
            <p className="text-xs text-muted-foreground">{portalNote}</p>
          </div>
        ) : null}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
